// aiBoomAggregators.js
//
// A COMPOSED theme, not a new dataset. Everything here already exists in
// ai_aggregators, nji_aggregator or accAggregators; this file pulls the four
// question sets the AI-boom reel needs into one groupsById so a single map
// embed can show all of them.
//
// WHY IT HAS TO EXIST: a reel mounts MainPage with one `slug`, and that slug
// resolves to exactly one theme config. The map can only render variables
// present in that config, so a story spanning AI sentiment (theme "ai"),
// AI-advertising reaction (theme "nji") and data centers (theme "acc_policy")
// needs them composed. gambling_aggregators.js does the same
// trick on a smaller scale by borrowing econ_approve from polAggregators.
//
// ── THE TIMING POINT, WHICH IS NOT OBVIOUS AND MATTERS ───────────────────────
// The wave NUMBERS differ between schemas but the fieldwork does not. ACC
// "wave 3" was fielded at the same time as tracking "wave 4" (July 2026). So
// the data-center answers and the wave-4 AI answers describe the same moment
// and can sit in one narrative as a single snapshot. Do NOT add a caveat that
// they are spliced from different periods: they are not. The only thing you
// cannot do is put them on one wave axis, because the numbering disagrees.
//
// Verified alignment: the ACC and tracking CD tables carry the same 436
// districts (tracking has one extra all-null DC placeholder row, dist_fips
// 1100, which every district count in the reel excludes), and both are
// projected on CD120, so district-level comparison across the two is
// like-for-like.
import { getAIConfig, getAIColours } from "./ai_aggregators";
import { getNJIConfig } from "./nji_aggregator";
// The data-center groups live in the POLICY config, not the politics one
// (accAggregators holds three configs in one file). DataCentersPage mounts
// slug="acc_policy" for the same reason.
import { getACCPolicyConfig } from "./accAggregators";

export function getAIBoomColours() {
  return getAIColours();
}

export function getAIBoomConfig() {
  const ai = getAIConfig().groupsById;
  const nji = getNJIConfig().groupsById;
  const acc = getACCPolicyConfig().groupsById;

  const groupsById = {
    // ── 1. AI sentiment, the only genuinely tracked series here ─────────────
    // waves [2,3,4]. The wave-4 columns were always in the tracking tables;
    // ai_aggregators used to declare [2,3] and hid a whole wave.
    ai_impact_life: ai.ai_impact_life,
    ai_impact_society: ai.ai_impact_society,

    // ── 2. Who actually uses AI. Wave 4 only. ───────────────────────────────
    // Borrowed from the NJI config, where it was added for the authenticity
    // reel. It is the mechanism behind the sentiment split, so it earns a slide
    // here rather than being background. Note the upstream key is the
    // unprefixed `ai_use`, unlike its nji_-prefixed siblings.
    ai_use: nji.ai_use,

    // ── 3. The commercial consequence. Wave 4 only. ─────────────────────────
    nji_ad_reaction: nji.nji_ad_reaction,

    // ── 4. Data centers: where the buildout physically lands. ───────────────
    // ACC schema. Same fieldwork as tracking wave 4 despite being numbered
    // wave 3 (see the header note).
    pol_dc_support_usa: acc.pol_dc_support_usa,
    pol_dc_support_local: acc.pol_dc_support_local,
    pol_dc_oppose_reasons_usa: acc.pol_dc_oppose_reasons_usa,
    pol_dc_oppose_reasons_local: acc.pol_dc_oppose_reasons_local,
    // Both sides of the reasons question. The reel's reasons slide toggles
    // between them and maps the leading one per district, so all four batteries
    // have to be here.
    pol_dc_support_reasons_usa: acc.pol_dc_support_reasons_usa,
    pol_dc_support_reasons_local: acc.pol_dc_support_reasons_local,
  };

  // A composed config is easy to break silently: rename a group upstream and
  // the reel loses a slide with no error until someone opens it. Fail loudly
  // in development instead.
  if (process.env.NODE_ENV !== "production") {
    const missing = Object.entries(groupsById)
      .filter(([, g]) => !g || !Array.isArray(g.keys) || g.keys.length === 0)
      .map(([k]) => k);
    if (missing.length) {
      // eslint-disable-next-line no-console
      console.error(
        `[aiBoomAggregators] these groups did not resolve from their source config: ${missing.join(", ")}. ` +
        "Something was renamed or removed upstream."
      );
    }
  }

  return { groupsById };
}

// Tab-as-config default export, matching the other aggregators.
const aiBoomTab = {
  tab: "aiboom",
  label: "AI & Data Centers",
  get metrics() { return getAIBoomConfig().groupsById; },
};

export default aiBoomTab;
